import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { useQuery } from 'react-query'
import { getLoanCandidates } from 'services/fund'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import useGuarantorSuggestion from 'pages/loans/manager/addNew/hooks/useGuarantorSuggestion'
import { faPlain, toman } from 'utilities/helper/format'
import WizardChrome, { IReceiptLine } from './components/WizardChrome'
import StickyAction from './components/StickyAction'
import StepBorrower from './steps/StepBorrower'
import StepAmount from './steps/StepAmount'
import StepGuarantor from './steps/StepGuarantor'
import StepTerms, { TermsSummary } from './steps/StepTerms'
import useGrantDraft from './useGrantDraft'
import StepReview from './steps/StepReview'
import useGrantSubmit, { IGrantDraft } from './useGrantSubmit'
import useWizardHistory from './useWizardHistory'

const TOTAL_STEPS = 5
const MIN_AMOUNT = 5_000

const STEP_TITLES: Record<number, { title: string; subtitle?: string }> = {
    1: {
        title: 'به چه کسی وام می‌دهید؟',
        subtitle:
            'اعضا بر اساس امتیاز اعتباری مرتب شده‌اند — یعنی چقدر پول، چند روز، در صندوق گذاشته‌اند. امتیاز فقط راهنماست؛ تصمیم با شماست.',
    },
    2: { title: 'مبلغ وام چقدر باشد؟' },
    3: {
        title: 'ضامن دارد؟',
        subtitle: 'این مرحله اختیاری است و می‌توانید ردش کنید.',
    },
    4: { title: 'چطور پس بدهد؟' },
    5: { title: 'بازبینی و ثبت' },
}

/**
 * اعطای وام, as a wizard.
 *
 * The screen this replaces was one long form whose first field was «مبلغ
 * وام». That is the second question. The first — «به چه کسی؟» — got a bare
 * name picker and no information at all, which meant the manager of a
 * 500-member fund answered the most consequential question on the screen
 * from memory.
 *
 * Each step asks one thing and then collapses into a line of the receipt at
 * the top, so nothing is hidden and every answer stays one tap away. The
 * step lives in the URL (`?step=`) rather than in state alone so the phone's
 * back button walks the wizard instead of abandoning it — losing four
 * answered questions to a stray back-swipe is the classic way a wizard gets
 * worse than the form it replaced.
 */
export default function GrantLoanWizard() {
    const params = useParams()
    const { state } = useLocation()
    const [searchParams] = useSearchParams()

    const moneypoolId = params?.id
    const step = Math.min(
        Math.max(Number(searchParams.get('step') || 1), 1),
        TOTAL_STEPS
    )

    // survives the trip to /manage-bank-cards/add and back
    const {
        borrower,
        setBorrower,
        amount,
        setAmount,
        terms,
        setTerms,
        guarantors,
        setGuarantors,
        note,
        setNote,
        clear: clearDraft,
    } = useGrantDraft(moneypoolId)
    // the card is deliberately NOT persisted: it is owned by useGetCards,
    // which re-reads the account list on the way back — exactly when the
    // manager has just added the account they were missing
    const [card, setCard] = useState<IBankAccountCards | undefined>()

    const suggestion = useGuarantorSuggestion(
        moneypoolId,
        borrower?.poolship_id
    )

    const draft: IGrantDraft = {
        moneypoolId,
        fundName: state?.name,
        borrower,
        amount,
        terms,
        guarantors,
        note,
        card,
        nudgeId: searchParams.get('nudge'),
    }
    const { submit, isSubmitting } = useGrantSubmit(draft, clearDraft)

    useEffect(() => {
        posthog.capture('loan_grant_step_viewed', {
            fund_id: moneypoolId,
            step,
        })
    }, [step, moneypoolId])

    // The wizard is one mounted component whose step lives in `?step=`, so the
    // effect above runs on every step change. «شروع» must not: it is the
    // denominator of the whole funnel, and counting it five times per grant
    // would put the completion rate at a fifth of the truth.
    const hasStarted = useRef(false)
    useEffect(() => {
        if (hasStarted.current) return
        hasStarted.current = true
        posthog.capture('loan_grant_started', {
            fund_id: moneypoolId,
            flow: 'wizard',
            // `src` is what the Bale nudge's deep link sets, so a grant that
            // started from a notification is separable from one that started
            // in the app — the whole point of the feature is the difference.
            entry_source:
                searchParams.get('src') ?? state?.source ?? 'unknown',
            nudge_id: searchParams.get('nudge') || null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moneypoolId])

    /**
     * The arrow, the receipt's «ویرایش» and the phone's back button walk ONE
     * linear stack of steps — see useWizardHistory.
     *
     * History worth keeping: the arrow was first `navigate(-1)`, which walked
     * FORWARD after a receipt jump (the stack was a log of visits); then it
     * pushed «step − 1», which fixed the arrow and made the phone's back
     * button bounce 2 → 3 → 2. Both are the same mistake — letting the stack
     * stop being «1, 2, …, current».
     *
     * `{ step }` alone on purpose: the one-shot `?borrower=` / `?nudge=` link
     * parameters are applied once and must not ride along to later steps.
     */
    const stepNav = useWizardHistory({
        step,
        paramsFor: (next) => ({ step: String(next) }),
    })
    const goTo = (next: number) => stepNav.forward(next)
    const back = stepNav.back

    const selectBorrower = (candidate: ILoanCandidate) => {
        applyBorrower(candidate, 'picker', {
            // whether the queue is worked in the order the members' own
            // deadlines imply, or in score order regardless — the whole
            // reason فوریت is now on the row
            demand_priority: candidate.demand?.priority || null,
            demand_has_note: !!candidate.demand?.description?.trim(),
        })
    }

    /**
     * «اعطای وام به این عضو» on کارنامهٔ عضو already answered step 1.
     *
     * The manager picked the person, then read a page about them, then asked
     * to lend to them — putting the name picker in front of them again asks
     * the one question they have most decisively already answered, and on a
     * 500-member fund it also asks them to find that name a second time.
     *
     * Applied once, on mount: after that the manager owns the wizard, and
     * re-running this would overwrite a borrower they deliberately changed on
     * a walk back to step 1. `replace` rather than push, so the back arrow
     * returns to the کارنامه they came from instead of to a step they were
     * never shown.
     */
    /**
     * Answer step 1 on the manager's behalf and move them to step 2.
     *
     * Shared by the two ways that can happen — «اعطای وام به این عضو» on
     * کارنامهٔ عضو (router state) and a Bale nudge button (a URL) — so the
     * two cannot drift on what prefilling means or which event is captured.
     *
     * `replace`, not push: the back arrow should return to wherever they came
     * from, not to a step they were never shown.
     */
    const applyBorrower = (
        candidate: ILoanCandidate,
        source: string,
        extra: Record<string, unknown> = {}
    ) => {
        setBorrower(candidate)
        // A member who filed a request already answered the next two
        // questions; prefilling is not a decision, it is not retyping.
        //
        // Written UNCONDITIONALLY, not only when there is a demand: the draft
        // outlives the screen in sessionStorage, so a wizard reopened on a
        // DIFFERENT borrower would otherwise keep the previous one's amount
        // and instalment count and present them as this member's request.
        setAmount(candidate.demand ? String(candidate.demand.amount) : '')
        setTerms((current) => ({
            ...current,
            installments: candidate.demand
                ? String(candidate.demand.num_repayments ?? '')
                : '',
        }))
        posthog.capture('loan_grant_step_completed', {
            fund_id: moneypoolId,
            step: 1,
            flow: 'wizard',
            from_demand: !!candidate.demand,
            preselected: source !== 'picker',
            preselect_source: source,
            // joins this click back to the exact LoanGrantNudge row that
            // offered the member, so "sent" and "opened" are one funnel
            // rather than two unrelated counters
            nudge_id: searchParams.get('nudge') || null,
            ...extra,
        })
        // From the picker this PUSHES, so the back arrow returns to the list
        // the manager was just reading. From a preselect it REPLACES, so back
        // leaves for wherever they came from rather than showing them a step
        // they were never given.
        if (source === 'picker') stepNav.forward(2)
        else stepNav.replace(2)
    }

    const preselected = useRef(false)
    useEffect(() => {
        if (preselected.current) return
        preselected.current = true
        const handed = state?.borrower as ILoanCandidate | undefined
        if (!handed) return
        // `source` says who handed it over — کارنامهٔ عضو, or the loan
        // request's own «بررسی و اعطای وام» (`cartable`), which is the case
        // the owner asked for: the manager is standing on one request, so
        // «به چه کسی؟» is already answered.
        applyBorrower(handed, state?.source || 'state')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * The same shortcut, but reachable from outside the app.
     *
     * The Bale «الان می‌توانی وام بدهی» message carries one glass button per
     * candidate, and a bot button can only hand over a URL — there is no
     * router `state` to put an ILoanCandidate in. So the link names the
     * borrower by poolship id and the row is fetched here.
     *
     * `requests_only=0`: the notification is sent about somebody with an open
     * request, but by the time the manager taps it that request may already
     * have been granted, which drops them out of the requests-only list. The
     * full list still has them, so the wizard opens on the right person and
     * simply asks the amount instead of dead-ending on «پیدا نشد».
     */
    const wantedPoolshipId = Number(searchParams.get('borrower')) || undefined
    // Compare IDENTITIES, not emptiness. `useGrantDraft` restores the draft
    // from sessionStorage on mount, so after the manager taps the first
    // candidate button in a Bale message the wizard already HAS a borrower.
    // With `!borrower` the query was then disabled for every later button in
    // that same message, the lookup never ran, and the wizard silently opened
    // on the FIRST button's member — reported from the very first real
    // message: tapping «حاج آقا لطیفی» or «امیرحسین فرامرزی» both landed on
    // «سلمه تیموری».
    const needsLookup =
        !!wantedPoolshipId && borrower?.poolship_id !== wantedPoolshipId
    const { data: lookup, isError: lookupFailed } = useQuery(
        ['loan-candidates', moneypoolId, false, false],
        getLoanCandidates,
        { enabled: !!moneypoolId && needsLookup, staleTime: 60_000 }
    )

    // Which link has been honoured, by borrower id rather than a bare boolean
    // — a second nudge button opened in the same session names a different
    // member and must still be applied.
    const linkAppliedFor = useRef<number | undefined>(undefined)
    useEffect(() => {
        if (!wantedPoolshipId) return
        if (linkAppliedFor.current === wantedPoolshipId) return
        if (borrower?.poolship_id === wantedPoolshipId) {
            // already the right person (restored draft, or we just applied it)
            linkAppliedFor.current = wantedPoolshipId
            return
        }
        if (lookupFailed) {
            linkAppliedFor.current = wantedPoolshipId
            toast.error('گیرندهٔ این لینک پیدا نشد؛ از فهرست انتخاب کنید.')
            return
        }
        if (!lookup) return
        const match = lookup.results?.find(
            (row) => row.poolship_id === wantedPoolshipId
        )
        linkAppliedFor.current = wantedPoolshipId
        if (!match) {
            toast.info('این درخواست دیگر باز نیست؛ از فهرست انتخاب کنید.')
            stepNav.replace(1)
            return
        }
        applyBorrower(match, searchParams.get('src') || 'link')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookup, lookupFailed, wantedPoolshipId, borrower])

    const receipt = useMemo<IReceiptLine[]>(() => {
        const lines: IReceiptLine[] = []
        if (step > 1 && borrower) {
            lines.push({
                label: 'گیرنده',
                value: borrower.member_name,
                step: 1,
            })
        }
        if (step > 2 && Number(amount) > 0) {
            lines.push({ label: 'مبلغ', value: toman(Number(amount)), step: 2 })
        }
        if (step > 3) {
            lines.push({
                label: 'ضامن',
                value: guarantors.length
                    ? guarantors.map((item) => item.name).join('، ')
                    : 'ندارد',
                step: 3,
            })
        }
        if (step > 4 && Number(terms.installments) > 0) {
            lines.push({
                label: 'اقساط',
                value: `${faPlain.format(Number(terms.installments))} قسط`,
                step: 4,
            })
        }
        return lines
    }, [step, borrower, amount, guarantors, terms])

    const canContinue = () => {
        if (step === 2) return Number(amount) >= MIN_AMOUNT
        if (step === 4) return Number(terms.installments) >= 1
        return true
    }

    const continueLabel = () => {
        if (step === 3 && guarantors.length === 0) return 'بدون ضامن ادامه بده'
        return 'ادامه'
    }

    const onContinue = () => {
        if (!canContinue()) {
            // the silent half of the funnel: the manager pressed «ادامه» and
            // the wizard refused. Only a toast said so, so a step people could
            // not get past looked exactly like a step they chose to abandon.
            posthog.capture('loan_grant_step_blocked', {
                fund_id: moneypoolId,
                step,
                reason:
                    step === 2 ? 'amount_below_min' : 'installments_missing',
            })
            if (step === 2) {
                toast.error('مبلغ وام را وارد کنید')
            } else if (step === 4) {
                toast.error('تعداد اقساط را وارد کنید')
            }
            return
        }
        posthog.capture('loan_grant_step_completed', {
            fund_id: moneypoolId,
            step,
            flow: 'wizard',
            from_demand: !!borrower?.demand,
        })
        goTo(step + 1)
    }

    // a deep link to a later step with nothing filled in is a dead end --
    // UNLESS a `?borrower=` lookup is still in flight, which is exactly what
    // the Bale nudge's link opens with. This guard runs on the first render,
    // before that fetch can resolve, and `setSearchParams` replaces the whole
    // query string: left unqualified it bounced to step 1 AND dropped the
    // `borrower` param, so the lookup could never complete and the button
    // silently landed the manager back on the name picker.
    useEffect(() => {
        if (step > 1 && !borrower && !wantedPoolshipId) {
            stepNav.replace(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, borrower, wantedPoolshipId])

    const { title, subtitle } = STEP_TITLES[step]

    return (
        <WizardChrome
            title={title}
            subtitle={subtitle}
            step={step}
            totalSteps={TOTAL_STEPS}
            receipt={receipt}
            onBack={back}
            onJumpTo={stepNav.backTo}
            tallFooter={step === 5}
        >
            {step === 1 && (
                <StepBorrower
                    moneypoolId={moneypoolId}
                    selectedPoolshipId={borrower?.poolship_id}
                    onSelect={selectBorrower}
                />
            )}
            {step === 2 && (
                <StepAmount
                    moneypoolId={moneypoolId}
                    amount={amount}
                    onChange={setAmount}
                    requestedAmount={borrower?.demand?.amount}
                />
            )}
            {step === 3 && (
                <StepGuarantor
                    moneypoolId={moneypoolId}
                    borrowerPoolshipId={borrower?.poolship_id}
                    borrowerName={borrower?.member_name}
                    suggested={suggestion}
                    value={guarantors}
                    onChange={setGuarantors}
                />
            )}
            {step === 4 && (
                <StepTerms
                    amount={Number(amount || 0)}
                    terms={terms}
                    onChange={setTerms}
                />
            )}
            {step === 5 && (
                <StepReview
                    moneypoolId={moneypoolId}
                    borrowerName={borrower?.member_name}
                    borrowerPoolshipId={borrower?.poolship_id}
                    borrowerMemberId={borrower?.member_id}
                    fundName={state?.name}
                    amount={Number(amount || 0)}
                    terms={terms}
                    guarantors={guarantors}
                    note={note}
                    onNoteChange={setNote}
                    onCardChange={setCard}
                    selectedCard={card}
                    demand={borrower?.demand ?? undefined}
                    requestedCardId={
                        borrower?.demand?.selected_card?.id ?? undefined
                    }
                    isSubmitting={isSubmitting}
                    onGrant={() => submit(false)}
                    onRecordOnly={() => submit(true)}
                />
            )}

            {/* step 1 advances by picking somebody and step 5 has its own two
                outcomes, so neither gets a «ادامه» bar */}
            {step > 1 && step < 5 && (
                <StickyAction
                    summary={
                        step === 4 ? (
                            <TermsSummary
                                amount={Number(amount || 0)}
                                terms={terms}
                            />
                        ) : step === 3 && guarantors.length > 0 ? (
                            // a second, unmissable confirmation of who was
                            // picked — the list can be long enough that the
                            // selected row scrolls out of sight
                            <p className=" rounded-xl bg-brand-soft px-3 py-2 text-[11px] font-bold text-brand-deep">
                                ضامن:{' '}
                                {guarantors.map((item) => item.name).join('، ')}
                            </p>
                        ) : undefined
                    }
                >
                    <button
                        type="button"
                        onClick={onContinue}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        {continueLabel()}
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
